import React, { ReactElement, useMemo, useState } from 'react';
import { List, RowComponentProps } from 'react-window';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { Box, useTheme } from '@mui/material';

import { Tooltip, Typography } from '@zendesk/forethought-elements';

const DEFAULT_COLUMN_WIDTH = 100;

interface BaseRowProps {
  /**
   * Optionally determine whether row can be clicked
   * Defaults to true
   */
  isClickable?: boolean;
  /**
   * Optionally include tagText to display a tag in the top left corner of the row
   */
  tagText?: string | null;
}

interface CardTableColumn<T> {
  columnWidth?: number;
  fixedWidth?: boolean;
  id: string;
  label: string;
  labelTooltip?: string;
  renderCell: (row: T) => React.ReactNode;
}

interface ListRowProps<T> {
  cellPadding: string;
  columns: CardTableColumn<T>[];
  hoverBorderColor: string;
  onClickRow: (row: T) => void;
  rows: (T & BaseRowProps)[];
  spacing: number;
}

function RowComponent<T>({
  cellPadding,
  columns,
  hoverBorderColor,
  index,
  onClickRow,
  rows,
  spacing,
  style,
}: RowComponentProps<ListRowProps<T>>): ReactElement {
  const { palette } = useTheme();
  const row = rows[index];
  if (!row) {
    return <Box sx={{ ...style }} />;
  }
  const { isClickable = true, tagText } = row;
  const onClick = isClickable ? () => onClickRow(row) : undefined;

  return (
    <Box style={{ ...style, paddingBottom: spacing }}>
      <Box
        onClick={onClick}
        sx={{
          ...(isClickable
            ? {
                '&:hover': {
                  border: `1px solid ${hoverBorderColor}`,
                  boxShadow:
                    '0px 0px 1px rgba(0, 12, 32, 0.04), 0px 2px 6px rgba(3, 17, 38, 0.107135)',
                },
              }
            : {}),
          alignItems: 'center',
          bgcolor: palette.colors.white,
          border: `1px solid ${palette.colors.slate[200]}`,
          borderRadius: '8px',
          boxSizing: 'border-box',
          cursor: isClickable ? 'pointer' : 'default',
          display: 'flex',
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {tagText && (
          <Box
            sx={theme => ({
              alignItems: 'flex-end',
              bgcolor: theme.palette.colors.yellow[500],
              display: 'flex',
              height: 90,
              justifyContent: 'center',
              padding: '6px',
              position: 'absolute',
              top: 0,
              transform: 'rotate(-45deg) translateY(-64px)',
              width: 90,
            })}
          >
            <Typography variant='font11'>{tagText}</Typography>
          </Box>
        )}
        {columns.map(({ columnWidth, fixedWidth, id, renderCell }) => (
          <Box
            key={id}
            sx={{
              flex: fixedWidth ? '0 0 auto' : 1,
              minWidth: fixedWidth
                ? undefined
                : columnWidth || DEFAULT_COLUMN_WIDTH,
              padding: fixedWidth ? '12px 8px' : cellPadding,
              width: fixedWidth
                ? columnWidth || DEFAULT_COLUMN_WIDTH
                : undefined,
            }}
          >
            {renderCell(row)}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

interface CardTableProps<T> {
  /**
   * Padding for each cell
   * Defaults to '24px'
   */
  cellPadding?: string;
  /**
   * Column data
   */
  columns: CardTableColumn<T>[];
  /**
   * Color of header text
   */
  headerColor?: string;
  /**
   * Typography variant for header text
   * Defaults to 'font14Bold'
   */
  headerVariant?: string;
  /**
   * Border color on row hover
   */
  hoverBorderColor?: string;
  /**
   * Height of each item must be set and must be the same for all items for virtualized list
   * Defaults to 90px
   */
  itemSize?: number;
  /**
   * Handle row click
   */
  onClickRow: (row: T) => void;
  /**
   * Row data
   */
  rows: (T & BaseRowProps)[];
  /** Override the calculated minimum width of the table in pixels. */
  minWidth?: number;
  /**
   * Whether to show the header border on scroll
   * Defaults to true
   */
  showHeaderBorder?: boolean;
  /**
   * Space between items
   * Defaults to 16px
   */
  spacing?: number;
}

export default function CardTable<T = unknown>({
  cellPadding = '24px',
  columns,
  headerColor,
  headerVariant = 'font14Bold',
  hoverBorderColor,
  itemSize = 90,
  minWidth: minWidthProp,
  onClickRow,
  rows,
  showHeaderBorder = true,
  spacing = 16,
}: CardTableProps<T>) {
  const [hasScrolled, setHasScrolled] = useState(false);
  const { palette } = useTheme();

  const resolvedHoverBorderColor = hoverBorderColor || palette.colors.grey[800];

  const minWidth = useMemo(
    () =>
      minWidthProp ??
      columns.reduce(
        (sum, col) => sum + (col.columnWidth || DEFAULT_COLUMN_WIDTH) + 48,
        0,
      ),
    [columns, minWidthProp],
  );

  return (
    <Box sx={{ minWidth }}>
      <Box
        sx={{
          alignItems: 'center',
          ...(showHeaderBorder
            ? {
                borderBottom: '1px solid',
                borderColor: hasScrolled
                  ? palette.colors.slate[300]
                  : 'transparent',
                transition: 'border-color 0.2s',
              }
            : {}),
          display: 'flex',
        }}
      >
        {columns.map(({ columnWidth, fixedWidth, id, label, labelTooltip }) => (
          <Box
            key={id}
            sx={{
              alignItems: 'center',
              display: 'flex',
              flex: fixedWidth ? '0 0 auto' : 1,
              gap: '4px',
              minWidth: fixedWidth
                ? undefined
                : columnWidth || DEFAULT_COLUMN_WIDTH,
              padding: fixedWidth ? '16px 8px' : '16px',
              width: fixedWidth
                ? columnWidth || DEFAULT_COLUMN_WIDTH
                : undefined,
            }}
          >
            <Typography
              color={headerColor}
              variant={headerVariant as 'font14Bold'}
            >
              {label}
            </Typography>
            {labelTooltip && (
              <Tooltip tooltipContent={labelTooltip}>
                <InfoIcon fontSize='small' />
              </Tooltip>
            )}
          </Box>
        ))}
      </Box>
      <List
        onScroll={() => {
          setHasScrolled(true);
        }}
        overscanCount={10}
        rowComponent={RowComponent<T>}
        rowCount={rows.length}
        rowHeight={itemSize + spacing}
        rowProps={{
          cellPadding,
          columns,
          hoverBorderColor: resolvedHoverBorderColor,
          onClickRow,
          rows,
          spacing,
        }}
        style={{ height: 'calc(100vh - 230px)' }}
      />
    </Box>
  );
}
